import { Injectable, InjectionToken, Optional, inject } from '@angular/core';
import { AuthTokenService } from '@kempoweroyj/ngx-auth-token-service';
import { AppConfigReceiptLookup } from '../interfaces/env-interfaces';
import { EnvService } from './env.service';

let AppConfig: AppConfigReceiptLookup;

/** Called from main.ts which loads config from /config.json */
export function setCsmsAppConfig(appConfig: AppConfigReceiptLookup) {
    AppConfig = appConfig;
}

// declare 404 path here to avoid circular dependency with app-shell
export const NOT_FOUND_PATH = 'page-not-found';

export type CustomerInURLMode = 'subDomain' | 'pathParam';

export const LOCAL_DEV_MODE = new InjectionToken<CustomerInURLMode>('CustomerInURLMode for local development', {
    providedIn: 'root',
    /* TOGGLE THIS for local development testin, default to pathParam
     * Set to 'pathParam' to test localhost:4200/test-domain
     * Set to 'subDomain' to test localhost:4200 with assumed extractedCustomerId LOCAL_DEV_CUSTOMER_ID_IN_DOMAIN
     */
    factory: () => 'pathParam',
});

const LOCAL_DEV_CUSTOMER_ID_IN_DOMAIN = 'test-domain';

/**
 *  This file defines the configuration service for the receipt lookup application.
 *  It provides methods to determine customer identifier from app URL and set up API URLs.
 *  This file structure is copied from libs/chargeye/app-shared/service-libs/libs/services/config/config.service.ts
 */
@Injectable({
    providedIn: 'root',
})
export class ConfigService {
    readonly #authToken: AuthTokenService = inject(AuthTokenService);
    readonly #envService: EnvService = inject(EnvService);
    readonly #localDevMode: CustomerInURLMode = inject(LOCAL_DEV_MODE, { optional: true }) || 'pathParam';

    constructor() {
        this.#authToken.restApiUrl = this.restApiUrl;
        if (this.#envService.isLocalHost()) {
            // Local dev: appBaseHostname is used for pathParam mode, irrelevant for subdomain mode
            this.config.appBaseHostname = 'localhost';
        }
    }

    get config(): AppConfigReceiptLookup {
        return AppConfig;
    }

    get restApiUrl(): string {
        return `https://${this.config.apiBaseUrl}/api`;
    }

    /**
     * Extracts the customer identifier from the app URL based on the configured appBaseHostname.
     * It supports both subdomain and path parameter modes.
     */
    get extractedCustomerId(): string | undefined {
        const { appBaseHostname } = this.config;
        const hostname = window?.location?.hostname;
        const path = window?.location?.pathname || '/';

        // Mode 1: Path param (host exactly the base; id is first path segment)
        if (this.isPathParamMode) {
            const firstPathSegment = path.split('/').filter(Boolean)[0];
            if (!firstPathSegment) {
                console.warn(
                    `Path '${path}' has no first segment, cannot extract customer id from base '${appBaseHostname}'`
                );
                return undefined;
            }
            // Special case for 404 page: avoid sending 'page-not-found' as customerId to Branding API
            if (firstPathSegment === NOT_FOUND_PATH) {
                return undefined;
            }
            return firstPathSegment;
        }

        // Mode 2: Subdomain (single subdomain before base)
        if (this.isSubDomainMode) {
            // Special case for localhost development
            if (this.#envService.isLocalHost()) {
                return LOCAL_DEV_CUSTOMER_ID_IN_DOMAIN;
            }

            // Remove the trailing .appBaseHostname
            const subdomains = hostname.replace(`.${appBaseHostname}`, '').split('.').filter(Boolean);

            if (subdomains.length !== 1) {
                console.warn(`Expected exactly one subdomain before base '${appBaseHostname}', got '${hostname}'`);
                return undefined;
            }
            return subdomains[0];
        }

        console.warn(
            `Hostname '${hostname}' does not match base '${appBaseHostname}' (neither path nor subdomain mode)`
        );
        return undefined;
    }

    // In production: from hostname in url, deduce if the app is running in pathParam or subDomain mode
    // In localhost: use injected token to manually set mode

    get isPathParamMode(): boolean {
        if (this.#envService.isLocalHost()) {
            return this.#localDevMode === 'pathParam';
        }
        return window?.location?.hostname === this.config.appBaseHostname;
    }

    get isSubDomainMode(): boolean {
        if (this.#envService.isLocalHost()) {
            return this.#localDevMode === 'subDomain';
        }
        return window?.location?.hostname.endsWith(`.${this.config.appBaseHostname}`);
    }
}
